/**
 * DGConfigLovEditorModal.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * LOV editor modal. No master config imports — all data via props.
 *
 * Key design:
 *   • masterLov prop  — original master items (for diff display only)
 *   • customLov prop  — user's current override (null = start from master)
 *   • onSave callback — returns full resolved LovItem[] to DGConfigModal,
 *     which writes it into snapshot.lovs[attrId].
 *   • For taxonomy:   cascade picker L1→L2→L3 driven by taxonomyHierarchy prop.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";

// ─── TYPES (exported — reused in DGConfigModal) ───────────────────────────────

export interface LovItem {
  id: string;
  label: string;
  description?: string;
  score?: number | null;
  jurisdiction?: string;
  full_title?: string;
  reversible?: boolean;
  _custom?: boolean;   // UI-only, stripped before snapshot save
}

export interface TaxonomyEntry {
  id?: string;
  l1: string;
  l2: string;
  l3: string;
}

export interface TaxonomyLevelConfig {
  useL1: boolean;
  useL2: boolean;
  useL3: boolean;
}

export const DEFAULT_TAXONOMY_CONFIG: TaxonomyLevelConfig = {
  useL1: true,
  useL2: true,
  useL3: true,
};

export interface LovEditorModalProps {
  show: boolean;
  onHide: () => void;
  attribute: { id: string; label: string; type: string; description?: string; lov: LovItem[] | null };
  masterLov: LovItem[];
  customLov: LovItem[] | null;
  onSave: (lov: LovItem[]) => void;
  taxonomyHierarchy: TaxonomyEntry[];
  taxonomyLevelConfig: TaxonomyLevelConfig;
  onTaxonomyLevelConfigChange: (cfg: TaxonomyLevelConfig) => void;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function getDiffStatus(item: LovItem, masterLov: LovItem[]): "master" | "added" | "edited" {
  const m = masterLov.find((x) => x.id === item.id);
  if (!m) return "added";
  if (m.label !== item.label || m.description !== item.description) return "edited";
  return "master";
}

// ─── TAXONOMY CASCADE PICKER ─────────────────────────────────────────────────

const TaxCascadePicker: React.FC<{
  hierarchy: TaxonomyEntry[];
  onSelect: (entry: TaxonomyEntry) => void;
}> = ({ hierarchy, onSelect }) => {
  const [l1, setL1] = useState("");
  const [l2, setL2] = useState("");
  const [l3, setL3] = useState("");

  const l1List = useMemo(() => [...new Set(hierarchy.map((h) => h.l1))].sort(), [hierarchy]);
  const l2List = useMemo(() => [...new Set(hierarchy.filter((h) => h.l1 === l1).map((h) => h.l2))].sort(), [hierarchy, l1]);
  const l3List = useMemo(() => [...new Set(hierarchy.filter((h) => h.l1 === l1 && h.l2 === l2).map((h) => h.l3))].sort(), [hierarchy, l1, l2]);

  const handleL1 = (v: string) => { setL1(v); setL2(""); setL3(""); };
  const handleL2 = (v: string) => { setL2(v); setL3(""); };
  const handleL3 = (v: string) => {
    setL3(v);
    const entry = hierarchy.find((h) => h.l1 === l1 && h.l2 === l2 && h.l3 === v);
    if (entry) onSelect(entry);
  };

  const colStyle: React.CSSProperties = { flex: 1, display: "flex", flexDirection: "column", borderRight: "1px solid #e2e8f0" };
  const hdr = (active: boolean): React.CSSProperties => ({
    padding: "7px 12px", background: active ? "#4f46e5" : "#f8fafc",
    color: active ? "#fff" : "#64748b", fontSize: 10, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.8px", borderBottom: "1px solid #e2e8f0",
  });
  const item = (sel: boolean): React.CSSProperties => ({
    padding: "7px 12px", cursor: "pointer", fontSize: 12.5, userSelect: "none",
    fontWeight: sel ? 600 : 400, color: sel ? "#4f46e5" : "#374151",
    background: sel ? "#eff6ff" : "transparent",
    borderLeft: sel ? "3px solid #4f46e5" : "3px solid transparent",
    transition: "all 0.1s",
  });

  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "flex", minHeight: 160 }}>
        {/* L1 */}
        <div style={colStyle}>
          <div style={hdr(true)}>L1 — Domain</div>
          <div style={{ overflowY: "auto", maxHeight: 160 }}>
            {l1List.length === 0
              ? <div style={{ padding: "10px 12px", fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>No taxonomy in snapshot</div>
              : l1List.map((v) => <div key={v} onClick={() => handleL1(v)} style={item(l1 === v)}>{v}</div>)
            }
          </div>
        </div>
        {/* L2 */}
        <div style={colStyle}>
          <div style={hdr(!!l1)}>L2 — Category</div>
          <div style={{ overflowY: "auto", maxHeight: 160 }}>
            {!l1
              ? <div style={{ padding: "10px 12px", fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>← Select domain</div>
              : l2List.map((v) => <div key={v} onClick={() => handleL2(v)} style={item(l2 === v)}>{v}</div>)
            }
          </div>
        </div>
        {/* L3 */}
        <div style={{ ...colStyle, borderRight: "none" }}>
          <div style={hdr(!!l2)}>L3 — Element</div>
          <div style={{ overflowY: "auto", maxHeight: 160 }}>
            {!l2
              ? <div style={{ padding: "10px 12px", fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>← Select category</div>
              : l3List.map((v) => <div key={v} onClick={() => handleL3(v)} style={item(l3 === v)}>{v}</div>)
            }
          </div>
        </div>
      </div>
      {l1 && l2 && l3 && (
        <div style={{ padding: "8px 12px", background: "#f0fdf4", borderTop: "1px solid #bbf7d0", fontSize: 12, color: "#166534", fontWeight: 500 }}>
          ✓ {l1} › {l2} › {l3}
        </div>
      )}
    </div>
  );
};

// ─── ITEM FORM (add / edit) ───────────────────────────────────────────────────

interface FormState { id: string; label: string; description: string; score: string; }

const ItemForm: React.FC<{
  initial?: FormState; onSave: (f: FormState) => void; onCancel: () => void; isEdit?: boolean;
}> = ({ initial, onSave, onCancel, isEdit }) => {
  const [f, setF] = useState<FormState>(initial ?? { id: "", label: "", description: "", score: "" });
  const valid = f.id.trim() && f.label.trim();
  const inp: React.CSSProperties = { width: "100%", padding: "7px 10px", fontSize: 12.5, border: "1px solid #e2e8f0", borderRadius: 6, outline: "none", background: "#fff", color: "#0f172a" };
  return (
    <div style={{ padding: "14px 16px", borderRadius: 8, border: "1px solid #bfdbfe", background: "#f0f9ff", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: "#1e40af", textTransform: "uppercase", letterSpacing: "0.7px" }}>{isEdit ? "Edit Item" : "Add New Item"}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>ID *</label>
          <input value={f.id} onChange={(e) => setF((x) => ({ ...x, id: e.target.value }))} disabled={isEdit} placeholder="e.g. High_Risk" style={{ ...inp, background: isEdit ? "#f8fafc" : "#fff" }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Label *</label>
          <input value={f.label} onChange={(e) => setF((x) => ({ ...x, label: e.target.value }))} placeholder="Display label" style={inp} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Description</label>
          <input value={f.description} onChange={(e) => setF((x) => ({ ...x, description: e.target.value }))} placeholder="Optional" style={inp} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Score (0–5)</label>
          <input type="number" min={0} max={5} step={0.5} value={f.score} onChange={(e) => setF((x) => ({ ...x, score: e.target.value }))} style={inp} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", padding: "6px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12.5, fontWeight: 500 }}>Cancel</button>
        <button onClick={() => valid && onSave(f)} disabled={!valid}
          style={{ all: "unset", boxSizing: "border-box", cursor: valid ? "pointer" : "not-allowed", padding: "6px 16px", borderRadius: 6, background: valid ? "#4f46e5" : "#e0e7ff", color: valid ? "#fff" : "#a5b4fc", fontSize: 12.5, fontWeight: 600 }}>
          {isEdit ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
};

// ─── MAIN EXPORT ─────────────────────────────────────────────────────────────

export const LovEditorModal: React.FC<LovEditorModalProps> = ({
  show, onHide, attribute, masterLov, customLov, onSave,
  taxonomyHierarchy, taxonomyLevelConfig, onTaxonomyLevelConfigChange,
}) => {
  const isTaxonomy = attribute.type === "taxonomy";
  const [items,   setItems]   = useState<LovItem[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [search,  setSearch]  = useState("");
  const [pendingTax, setPendingTax] = useState<TaxonomyEntry | null>(null);

  useEffect(() => {
    if (!show) return;
    setItems((customLov ?? masterLov).map((x) => ({ ...x })));
    setAddOpen(false); setEditIdx(null); setSearch(""); setPendingTax(null);
  }, [show]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((i) => i.label.toLowerCase().includes(q) || (i.description ?? "").toLowerCase().includes(q));
  }, [items, search]);

  const added   = items.filter((i) => !masterLov.find((m) => m.id === i.id)).length;
  const edited  = items.filter((i) => { const m = masterLov.find((x) => x.id === i.id); return m && (m.label !== i.label || m.description !== i.description); }).length;
  const removed = masterLov.filter((m) => !items.find((i) => i.id === m.id)).length;

  const moveItem = (idx: number, dir: -1 | 1) => {
    const a = [...items]; const t = idx + dir;
    if (t < 0 || t >= a.length) return;
    [a[idx], a[t]] = [a[t], a[idx]]; setItems(a);
  };

  const addItem = (f: FormState) => {
    setItems((p) => [...p, { id: f.id.trim(), label: f.label.trim(), description: f.description.trim() || undefined, score: f.score ? parseFloat(f.score) : undefined, _custom: true }]);
    setAddOpen(false);
  };

  const updateItem = (idx: number, f: FormState) => {
    setItems((p) => p.map((x, i) => i !== idx ? x : { ...x, label: f.label.trim(), description: f.description.trim() || undefined, score: f.score ? parseFloat(f.score) : x.score }));
    setEditIdx(null);
  };

  const removeItem = (idx: number) => setItems((p) => p.filter((_, i) => i !== idx));

  const addTaxItem = () => {
    if (!pendingTax) return;
    const id = pendingTax.id ?? [pendingTax.l1, pendingTax.l2, pendingTax.l3].join("__");
    if (items.find((i) => i.id === id)) return;
    setItems((p) => [...p, { id, label: pendingTax.l3 || pendingTax.l2 || pendingTax.l1, description: `${pendingTax.l1} › ${pendingTax.l2} › ${pendingTax.l3}` }]);
    setPendingTax(null);
  };

  const handleSave = () => {
    // Strip UI-only _custom flag before writing to snapshot
    onSave(items.map(({ _custom, ...rest }) => rest));
  };

  const sc = (s?: number | null) => s == null ? { c: "#94a3b8", bg: "#f8fafc" } : s >= 4 ? { c: "#dc2626", bg: "#fef2f2" } : s >= 3 ? { c: "#d97706", bg: "#fffbeb" } : { c: "#16a34a", bg: "#f0fdf4" };

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "14px 20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Modal.Title style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
            {isTaxonomy ? "🗂 Edit Taxonomy Values" : `✎ Edit Allowed Values — ${attribute.label}`}
          </Modal.Title>
          <div style={{ display: "flex", gap: 7 }}>
            {added   > 0 && <span style={{ fontSize: 10.5, padding: "1px 8px", borderRadius: 4, background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" }}>+{added} added</span>}
            {removed > 0 && <span style={{ fontSize: 10.5, padding: "1px 8px", borderRadius: 4, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>−{removed} removed</span>}
            {edited  > 0 && <span style={{ fontSize: 10.5, padding: "1px 8px", borderRadius: 4, background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" }}>✎ {edited} edited</span>}
            {!added && !removed && !edited && <span style={{ fontSize: 10.5, padding: "1px 8px", borderRadius: 4, background: "#f8fafc", color: "#94a3b8", border: "1px solid #e2e8f0" }}>No changes from master</span>}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: "16px 20px", background: "#f8fafc" }}>
        {/* Taxonomy level toggles */}
        {isTaxonomy && (
          <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "#fff", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#475569" }}>Levels used:</span>
            {(["useL1", "useL2", "useL3"] as const).map((k, i) => (
              <span key={k} onClick={() => onTaxonomyLevelConfigChange({ ...taxonomyLevelConfig, [k]: !taxonomyLevelConfig[k] })}
                style={{ cursor: "pointer", fontSize: 12, padding: "3px 12px", borderRadius: 20, border: "1px solid", userSelect: "none",
                  background:  taxonomyLevelConfig[k] ? "#4f46e5" : "#f8fafc",
                  color:       taxonomyLevelConfig[k] ? "#fff"    : "#64748b",
                  borderColor: taxonomyLevelConfig[k] ? "#4f46e5" : "#e2e8f0" }}>
                L{i + 1}{taxonomyLevelConfig[k] ? " ✓" : ""}
              </span>
            ))}
            <span style={{ marginLeft: "auto", fontSize: 11.5, color: "#94a3b8" }}>{items.length} in snapshot</span>
          </div>
        )}

        {/* Taxonomy cascade picker */}
        {isTaxonomy && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: "#475569", marginBottom: 8 }}>Add from hierarchy:</div>
            <TaxCascadePicker hierarchy={taxonomyHierarchy} onSelect={setPendingTax} />
            {pendingTax && (
              <button onClick={addTaxItem}
                style={{ all: "unset", boxSizing: "border-box", marginTop: 8, cursor: "pointer", padding: "7px 16px", borderRadius: 7, background: "#4f46e5", color: "#fff", fontSize: 13, fontWeight: 600 }}>
                + Add to snapshot
              </button>
            )}
          </div>
        )}

        {/* Search */}
        <div style={{ marginBottom: 10 }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 7, outline: "none", background: "#fff", color: "#0f172a" }} />
        </div>

        {/* Item list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
          {filtered.map((itm, idx) => {
            const diff = getDiffStatus(itm, masterLov);
            const s = sc(itm.score);
            if (editIdx === idx) {
              return <ItemForm key={itm.id} isEdit initial={{ id: itm.id, label: itm.label, description: itm.description ?? "", score: itm.score != null ? String(itm.score) : "" }}
                onSave={(f) => updateItem(idx, f)} onCancel={() => setEditIdx(null)} />;
            }
            return (
              <div key={itm.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", borderRadius: 8, background: "#fff",
                border: diff === "added" ? "1px solid #bbf7d0" : diff === "edited" ? "1px solid #fde68a" : "1px solid #e2e8f0" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                  <button onClick={() => moveItem(idx, -1)} style={{ all: "unset", cursor: "pointer", color: "#94a3b8", fontSize: 10, padding: "2px 4px" }}>▲</button>
                  <button onClick={() => moveItem(idx, 1)}  style={{ all: "unset", cursor: "pointer", color: "#94a3b8", fontSize: 10, padding: "2px 4px" }}>▼</button>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{itm.label}</span>
                    <span style={{ fontSize: 10.5, color: "#94a3b8", fontFamily: "monospace" }}>{itm.id}</span>
                    {diff === "added"  && <span style={{ fontSize: 9.5, padding: "1px 6px", borderRadius: 3, background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" }}>+ new</span>}
                    {diff === "edited" && <span style={{ fontSize: 9.5, padding: "1px 6px", borderRadius: 3, background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" }}>✎ edited</span>}
                    {itm.score != null && <span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 7px", borderRadius: 4, background: s.bg, color: s.c }}>score {itm.score}</span>}
                    {itm.jurisdiction && <span style={{ fontSize: 10.5, padding: "1px 7px", borderRadius: 4, background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd" }}>{itm.jurisdiction}</span>}
                  </div>
                  {itm.description && <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 3, lineHeight: 1.45 }}>{itm.description}</div>}
                </div>
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  {!isTaxonomy && (
                    <button onClick={() => setEditIdx(idx)}
                      style={{ all: "unset", cursor: "pointer", padding: "4px 10px", borderRadius: 5, border: "1px solid #e2e8f0", background: "#f8fafc", fontSize: 11.5, color: "#374151" }}>Edit</button>
                  )}
                  {(isTaxonomy || !masterLov.find((m) => m.id === itm.id)) && (
                    <button onClick={() => removeItem(idx)}
                      style={{ all: "unset", cursor: "pointer", padding: "4px 10px", borderRadius: 5, border: "1px solid #fecaca", background: "#fef2f2", fontSize: 11.5, color: "#dc2626" }}>✕</button>
                  )}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 13, fontStyle: "italic" }}>
              {search ? "No items match." : "No items yet."}
            </div>
          )}
        </div>

        {/* Add new (non-taxonomy) */}
        {!isTaxonomy && (
          addOpen
            ? <ItemForm onSave={addItem} onCancel={() => setAddOpen(false)} />
            : <button onClick={() => setAddOpen(true)}
                style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", padding: "8px 16px", borderRadius: 7, border: "1px dashed #bfdbfe", background: "#f8fafc", color: "#3b82f6", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                + Add custom item
              </button>
        )}
      </Modal.Body>

      <Modal.Footer style={{ background: "#fff", borderTop: "1px solid #e2e8f0", padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#94a3b8" }}>{items.length} items will be saved in snapshot</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onHide} style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", padding: "7px 16px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontSize: 13, fontWeight: 500 }}>Cancel</button>
          <button onClick={handleSave} style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", padding: "7px 20px", borderRadius: 7, background: "#4f46e5", color: "#fff", fontSize: 13, fontWeight: 600 }}>✓ Save to Snapshot</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};